import { api, makeApiRequest } from 'api/_init'
import { unwrapEnvelope } from 'domain/shared/envelope'
import {
  mapEventInsights,
  mapCampaignResults,
  mapInsightsBreakdown,
  mapTopPerformingAds,
  mapAdDetails,
  mapEventEvaluationError,
} from './mappers'

function buildQuery(params) {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  if (!entries.length) return ''
  return '?' + entries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&')
}

const eventEvaluationApi = {
  fetchInsightsAsync: async (eventId, platform = 'all') => {
    const url = `/integrations/events/${eventId}/insights${buildQuery({ platform })}`
    const response = await makeApiRequest(api, 'get', url)
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapEventInsights(data), status }
    }
    return { error: mapEventEvaluationError(error, status), status }
  },

  // The two documented 400s (marketing_too_short_to_evaluate,
  // evaluation_unavailable_yet — the latter carrying min_days_required) come
  // back through the same error path as any other failure here; mapper
  // preserves error_code/data rather than collapsing them.
  fetchCampaignResultsAsync: async (eventId, platform = 'all') => {
    const url = `/integrations/events/${eventId}/campaign_results${buildQuery({ platform })}`
    const response = await makeApiRequest(api, 'get', url)
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapCampaignResults(data), status }
    }
    return { error: mapEventEvaluationError(error, status), status }
  },

  // breakdownBy takes the same 'ads'/'adsets' values InsightsBreakdown.jsx
  // already sends today — see schemas.js's BreakdownBy note, no v3→v4 value
  // mapping needed. Results from both platforms are merged/sorted
  // server-side before pagination — don't re-sort or re-paginate client-side.
  fetchInsightsBreakdownAsync: async (
    eventId,
    breakdownBy,
    { platform = 'all', page = 1, limit = 30, sortBy, sortOrder } = {},
  ) => {
    const url = `/integrations/events/${eventId}/insights/breakdown${buildQuery({
      platform,
      breakdown_by: breakdownBy,
      page,
      limit,
      sort_by: sortBy,
      sort_order: sortOrder,
    })}`
    const response = await makeApiRequest(api, 'get', url)
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapInsightsBreakdown(data), status }
    }
    return { error: mapEventEvaluationError(error, status), status }
  },

  fetchTopPerformingAdsAsync: async (eventId, { platform = 'all', limit = 5 } = {}) => {
    const url = `/integrations/events/${eventId}/insights/top-performing-ads${buildQuery({ platform, limit })}`
    const response = await makeApiRequest(api, 'get', url)
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapTopPerformingAds(data), status }
    }
    return { error: mapEventEvaluationError(error, status), status }
  },

  // GET /insights/ad-details?platform=&ad_id= — platform is required (the
  // same ad_id could in principle collide across meta/google), per the live
  // v4 endpoint.
  fetchAdDetailsAsync: async (eventId, adId, platform) => {
    const url = `/integrations/events/${eventId}/insights/ad-details${buildQuery({ platform, ad_id: adId })}`
    const response = await makeApiRequest(api, 'get', url)
    const { data, error, status } = unwrapEnvelope(response)
    if (status === 200) {
      return { data: mapAdDetails(data), status }
    }
    return { error: mapEventEvaluationError(error, status), status }
  },
}

export default eventEvaluationApi
